/**
 * EventLiveSession — an online or hybrid event's session, watched here.
 *
 * Absent entirely unless the organiser has put the event on air, so an
 * in-person event's page is unchanged. Polls rather than guesses: the server
 * decides whether a picture is actually arriving.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Radio } from 'lucide-react';
import { LiveStream } from '@/components/live/LiveStream';
import { BroadcastInfoPanel } from '@/components/live/BroadcastInfoPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const EventLiveSession = ({ eventId }) => {
  const { data } = useQuery({
    queryKey: ['event-live', eventId],
    queryFn: () => fetch(`${API_URL}/api/events/${eventId}/live`)
      .then((r) => (r.ok ? r.json() : null)),
    enabled: !!eventId,
    refetchInterval: 20000,
    retry: false,
  });

  if (!data?.live) return null;

  const player = data.player || {};
  return (
    <div className="mb-6" data-testid="event-live-section">
      <div className="flex items-center gap-2 mb-2">
        {data.watchable ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-600 px-2.5 py-1 text-xs font-semibold text-white"
                data-testid="event-live-badge">
            <span className="h-2 w-2 rounded-full bg-white animate-pulse" />LIVE NOW
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/20 text-amber-300 px-2.5 py-1 text-xs font-semibold"
                data-testid="event-live-starting">
            <Radio className="h-3 w-3" />Starting shortly
          </span>
        )}
        {data.title && (
          <span className="text-sm text-[color:var(--fwm-body)]" data-testid="event-live-title">
            {data.title}
          </span>
        )}
      </div>

      {player.kind === 'livekit' && (
        <LiveStream scope="event" ident={eventId} title={data.title}
                    testId="event-livekit" />
      )}
      {player.kind === 'iframe' && (
        <div className="aspect-video w-full bg-black rounded-xl overflow-hidden">
          <iframe title={data.title || 'Live session'} src={player.src}
                  className="w-full h-full" allowFullScreen
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture"
                  data-testid="event-live-iframe" />
        </div>
      )}
      {player.kind === 'video' && (
        <div className="aspect-video w-full bg-black rounded-xl overflow-hidden">
          <video src={player.src} controls autoPlay className="w-full h-full"
                 data-testid="event-live-video" />
        </div>
      )}
      {player.kind === 'none' && (
        <div className="rounded-xl bg-slate-900 p-6 text-center" data-testid="event-live-none">
          <p className="text-slate-200 text-sm">{player.reason}</p>
        </div>
      )}

      <BroadcastInfoPanel scope="event" ident={eventId} tone="light"
                          live={!!data.watchable} />
    </div>
  );
};

export default EventLiveSession;
